import { MutateResultCallbacks, useGetData, usePostData } from "./api";

export interface LineageDto {
  nodes: {
    id: string;
    config_schedule?: {
      cron: string;
    };
    config_trigger?: {
      trigger_on: {
        service_name: string;
        slug?: string | null;
        source_type?: string | null;
      }[];
    };
  }[];
  links: {
    source: string;
    target: string;
  }[];
}

interface GrindorLineageResponse {
  success: boolean;
  lineage_graph: LineageDto;
}

export const useGetLineage = (
  customer: string,
  product: string,
  version: number | -1
) => {
  const path = `/${customer}/${product}/grindor/lineage${
    version && version !== -1 ? `?version=${version}` : ""
  }`;
  return useGetData<GrindorLineageResponse>(path, { cacheKey: path });
};

interface GrindorSlugsResponse {
  success: boolean;
  slugs: string[];
}

interface GrindorWarehouseGroupsResponse {
  success: boolean;
  warehouse_groups: string[];
}

export const useGetGrindorWarehouseGroups = () => {
  const path = `/grindor/dbt_warehouse_groups`;
  return useGetData<GrindorWarehouseGroupsResponse>(path, { cacheKey: path });
};

export const useGetGrindorSlugs = (
  customer: string,
  product: string,
  version: number | -1
) => {
  const path = `/${customer}/${product}/grindor/slugs${
    version && version !== -1 ? `?version=${version}` : ""
  }`;
  return useGetData<GrindorSlugsResponse>(path, { cacheKey: path });
};

export const useGetAllGrindorSlugs = () => {
  const path = `/grindor/slugs`;
  return useGetData<GrindorSlugsResponse>(path, { cacheKey: path });
};

export const useGetGrindorSlugProducts = (slug: string) => {
  const path = `/grindor/slug-customers/${slug}`;
  return useGetData<GrindorSlugsResponse>(path, { cacheKey: path });
};

export interface GrindorStatusDto {
  state: string;
  slug: string;
  time_created: string;
}

interface GrindorStatusResponse {
  success: boolean;
  statuses: { [slug: string]: GrindorStatusDto[] };
}

export const useGetStatus = (
  customer: string,
  product: string,
  version: number | -1
) => {
  const path = `/${customer}/${product}/grindor/statuses${
    version && version !== -1 ? `?version=${version}` : ""
  }`;
  return useGetData<GrindorStatusResponse>(path, { cacheKey: path });
};

export interface GrindorTriggerBody {
  slugs: string[];
  force_trigger: boolean;
  full_refresh: boolean;
  warehouse_group: string | null;
  ignore_lineage: boolean;
  user: string;
}

interface GrindorTriggerResponse {
  success: boolean;
}

export const useManualTrigger = (
  customer: string,
  product: string,
  version: number | -1,
  options?: MutateResultCallbacks<GrindorTriggerResponse>
) => {
  const path = `/${customer}/${product}/grindor/trigger${
    version && version !== -1 ? `?version=${version}` : ""
  }`;
  return usePostData<GrindorTriggerResponse, GrindorTriggerBody>(path, options);
};

export interface GrindorMultiSelectTriggerBody {
  slug: string;
  products: string[];
  warehouse_group: string | null;
  full_refresh: boolean;
  force_trigger: boolean;
  ignore_lineage: boolean;
  user: string;
}

export const useMultiSelectManualTrigger = (
  options?: MutateResultCallbacks<GrindorTriggerResponse>
) => {
  const path = `/grindor/trigger`;
  return usePostData<GrindorTriggerResponse, GrindorMultiSelectTriggerBody>(
    path,
    options
  );
};

interface GrindorVersionsDto {
  success: boolean;
  data: number[];
}

export const useGrindorVersions = (customer: string, product: string) => {
  const path = `/${customer}/${product}/normalization/versions`;
  const { data, loading, ...rest } = useGetData<GrindorVersionsDto>(path, {
    cacheKey: path,
  });

  if (!data || loading) {
    return { data: { success: false, data: [-1] }, loading, ...rest };
  }

  return { data: { ...data, data: [-1, ...data.data] }, loading, ...rest };
};

interface GrindorJobsResponse {
  success: boolean;
  jobs: {
    id: string;
    state: string;
    created_at: string;
    updated_at: string;
    [key: string]: any; // Additional job properties
  }[];
}

export const useGetJobs = (
  customer: string,
  product: string,
  startDate?: string,
  endDate?: string,
  state?: string,
  version?: number,
  slug?: string
) => {
  const queryParams = new URLSearchParams();
  if (startDate) queryParams.append("start_date", startDate);
  if (endDate) queryParams.append("end_date", endDate);
  if (state) queryParams.append("state", state);
  if (version && version !== -1)
    queryParams.append("version", version.toString());
  if (slug) queryParams.append("slug", slug);

  const path = `/${customer}/${product}/grindor/jobs${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  return useGetData<GrindorJobsResponse>(path, { cacheKey: path });
};
